import { faWarning } from '@fortawesome/pro-regular-svg-icons'
import { Alert, Box, Flex, Loader } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

import { useMutationGenerateContract } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { Icon, Modal, useModal, useNotifications } from '@flecto/ui'

import { DocumentContractPreview } from '../../../booking-documents/components/DocumentContractPreview/DocumentContractPreview'
import {
  ModalViewSingleDocument,
  type ModalViewSingleDocumentProps,
} from '../../../booking-documents/components/ModalViewSingleDocument/ModalViewSingleDocument'
import { useBookingDocumentTranslation } from '../../../booking-documents/hooks/useBookingDocumentTranslation'
import { useContractTitle } from '../../../booking-documents/hooks/useContractTitle'
import { useDocumentState } from '../../../booking-documents/hooks/useDocumentState'
import {
  fetchDocumentData,
  serializeHTMLAndCSS,
} from '../../../booking-documents/services/documentDataService'

export type ModalBookingActionCreateContractProps = {
  bookingId?: ParameterID
}

export function ModalBookingActionCreateContract({
  bookingId,
}: Readonly<ModalBookingActionCreateContractProps>) {
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)

  const [hasError, setHasError] = useState(false)
  const [wasGenerated, setWasGenerated] = useState(false)
  const previewRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation('saas', { keyPrefix: 'booking_actions' })

  const { showNotification } = useNotifications()
  const { documentPreview, isLoaded } = useDocumentState()
  const { getContractTitle } = useContractTitle()

  const { mutateAsync: generateContract } = useMutationGenerateContract()

  const { openModal: openModalDocument } =
    useModal<ModalViewSingleDocumentProps>({
      modalComponent: (props) => (
        <ModalViewSingleDocument
          id={bookingId ?? ''}
          documentId={props?.documentId ?? ''}
        />
      ),
    })

  const { useTranslation: useBookingDocumentNameTranslation } =
    useBookingDocumentTranslation({
      bookingId,
    })

  const { t: translate } = useBookingDocumentNameTranslation()

  useEffect(() => {
    const initializeDocumentData = async () => {
      if (!bookingId) return

      try {
        setIsLoading(true)
        await fetchDocumentData({
          bookingId: String(bookingId),
          documentType: 'contract',
        })
      } catch (error) {
        console.error('Error initializing document data:', error)
        setHasError(true)
        showNotification({
          type: 'error',
          message: t('notification_action_create_contract_error'),
        })
      }
    }

    initializeDocumentData()
  }, [bookingId])

  useEffect(() => {
    const generateDocument = async () => {
      if (
        !documentPreview ||
        !previewRef.current ||
        wasGenerated ||
        !isLoaded ||
        !isLoading ||
        isGenerating
      ) {
        return
      }

      try {
        setIsGenerating(true)

        const documentHtml = await serializeHTMLAndCSS(
          previewRef.current,
          translate('label_contract_id', {
            replace: { id: getContractTitle() },
          }).trim()
        )

        const response = await generateContract({
          bookingId: String(bookingId ?? ''),
          contractAttributes: {
            visible_to_renter: true,
            content: documentHtml,
            filename_base: documentPreview?.booking_settings?.contract_title
              ? getContractTitle()
              : translate('label_contract_id', {
                  replace: { id: getContractTitle() },
                }).trim(),
            options: {
              folder_name: 'flecto_contract',
              html_header: '<div style="width: 100%; height: 40px;"></div>',
              html_footer: '<div style="width: 100%; height: 40px;"></div>',
            },
          },
        })

        if (!response?.booking_document?.id) {
          console.error(
            'Error generating document: No booking_document.id received from API.'
          )
          setHasError(true)
          showNotification({
            type: 'error',
            message: t('notification_action_create_contract_error'),
          })
          return
        }

        setWasGenerated(Boolean(response.booking_document.id))

        showNotification({
          type: 'success',
          message: t('notification_action_create_contract_success'),
        })

        openModalDocument({ documentId: response.booking_document.id })
      } catch (error) {
        console.error('Error generating contract:', error)
        setHasError(true)
        showNotification({
          type: 'error',
          message: t('notification_action_create_contract_error'),
        })
      } finally {
        setIsGenerating(false)
        setIsLoading(false)
      }
    }

    generateDocument()
  }, [documentPreview, bookingId, wasGenerated, isLoading, isGenerating, t])

  useEffect(() => {
    return () => {
      useDocumentState.getState().reset()
    }
  }, [])

  return (
    <Modal size='md'>
      <Modal.Title>{t('modal_action_create_contract_title')}</Modal.Title>
      <Modal.Content mb='lg'>
        <Box display='none'>
          <Box data-testid='contract-preview-container' ref={previewRef}>
            {documentPreview && <DocumentContractPreview />}
          </Box>
        </Box>

        {hasError && (
          <Alert color='red' icon={<Icon icon={faWarning} />}>
            {t('modal_action_create_contract_error')}
          </Alert>
        )}
        {!hasError && (isLoading || isGenerating) && (
          <Flex align='center' justify='center' style={{ overflow: 'hidden' }}>
            <Loader size='md' data-testid='loader' />
          </Flex>
        )}
      </Modal.Content>
      {hasError && <Modal.Close />}
    </Modal>
  )
}
